/**
 * Navigation Context
 *
 * React context and hooks for navigation state and utilities
 */

import {
  createContext,
  useContext,
  useMemo,
  ComponentType,
  ReactNode,
} from 'react';
import {
  useNavigate,
  useLocation,
  useParams,
  useSearchParams,
} from 'react-router-dom';
import {
  NavigationContext,
  NavigateOptions,
  RouteExtension,
  getRouteRegistry,
} from '../ui-extension-system';
import { useBreadcrumbs } from './breadcrumbs';

/**
 * Navigation context instance
 */
const NavContext = createContext<NavigationContext | null>(null);

/**
 * Navigation provider props
 */
export interface NavigationProviderProps {
  /**
   * Children components
   */
  children: ReactNode;
}

/**
 * Navigation Provider Component
 *
 * Provides navigation context to child components
 */
export const NavigationProvider: ComponentType<NavigationProviderProps> = ({
  children,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();
  const [searchParams] = useSearchParams();
  const breadcrumbs = useBreadcrumbs();

  // Convert search params to object
  const query = useMemo(() => {
    const queryObj: Record<string, string> = {};
    searchParams.forEach((value, key) => {
      queryObj[key] = value;
    });
    return queryObj;
  }, [searchParams]);

  // Get current route from registry
  const currentRoute = useMemo(() => {
    const registry = getRouteRegistry();
    return registry.getRouteByPath(location.pathname);
  }, [location.pathname]);

  // Navigation helper
  const navigateTo = (path: string, options: NavigateOptions = {}) => {
    const { replace, state, query: queryParams } = options;

    // Build query string
    let finalPath = path;
    if (queryParams) {
      const params = new URLSearchParams(queryParams);
      finalPath = `${path}?${params.toString()}`;
    }

    navigate(finalPath, { replace, state });
  };

  // Go back
  const goBack = () => {
    navigate(-1);
  };

  // Go forward
  const goForward = () => {
    navigate(1);
  };

  const contextValue: NavigationContext = {
    currentRoute,
    params: params as Record<string, string>,
    query,
    breadcrumbs,
    navigate: navigateTo,
    goBack,
    goForward,
  };

  return <NavContext.Provider value={contextValue}>{children}</NavContext.Provider>;
};

/**
 * useNavigation hook
 *
 * Access navigation context
 *
 * @returns Navigation context
 *
 * @example
 * ```tsx
 * const { navigate, currentRoute, breadcrumbs } = useNavigation();
 * navigate('/users', { query: { page: '1' } });
 * ```
 */
export const useNavigation = (): NavigationContext => {
  const context = useContext(NavContext);

  if (!context) {
    throw new Error('useNavigation must be used within NavigationProvider');
  }

  return context;
};

/**
 * useCurrentRoute hook
 *
 * Get current route from registry
 *
 * @returns Current route extension or undefined
 */
export const useCurrentRoute = (): RouteExtension | undefined => {
  const { currentRoute } = useNavigation();
  return currentRoute;
};

/**
 * useQueryParams hook
 *
 * Get query parameters as object
 *
 * @returns Query parameters object
 */
export const useQueryParams = (): Record<string, string> => {
  const { query } = useNavigation();
  return query;
};

/**
 * useRouteParams hook
 *
 * Get route parameters
 *
 * @returns Route parameters object
 */
export const useRouteParams = (): Record<string, string> => {
  const { params } = useNavigation();
  return params;
};
